/** Shared display constants for scheduled-task components.
 *
 * Status/run-status tag colors and target-type icons live here so
 * `ScheduledTaskList` and `ScheduledTaskDetailDrawer` render identical
 * badges instead of duplicating the mapping. Chinese labels themselves stay
 * in `types/scheduledTask.ts` (`_LABELS` maps) — this file only adds the
 * visual (color/icon) layer on top. */

import type { ReactNode } from "react";
import { BellOutlined, RobotOutlined, ScheduleOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { isApiError } from "../../api";
import { mapApiError } from "../../utils/api-error";
import type {
  ScheduledRunReviewStatus,
  ScheduledTaskRunStatus,
  ScheduledTaskStatus,
  ScheduledTaskTargetType,
} from "../../types/scheduledTask";

export const STATUS_TAG_COLOR: Record<ScheduledTaskStatus, string> = {
  active: "green",
  paused: "orange",
  disabled: "default",
  finished: "default",
};

export const RUN_STATUS_TAG_COLOR: Record<ScheduledTaskRunStatus, string> = {
  triggered: "processing",
  running: "processing",
  completed: "success",
  failed: "error",
  skipped: "default",
};

/** 期产物人审状态徽标色（波次 2）。 */
export const RUN_REVIEW_STATUS_TAG_COLOR: Record<ScheduledRunReviewStatus, string> = {
  pending_review: "gold",
  accepted: "green",
  returned: "red",
};

export const TARGET_TYPE_ICON: Record<ScheduledTaskTargetType, ReactNode> = {
  pipeline_instance: <ThunderboltOutlined />,
  matter_reminder: <BellOutlined />,
  pipeline_template: <ScheduleOutlined />,
  agent_run: <RobotOutlined />,
};

export const TARGET_TYPE_TAG_COLOR: Record<ScheduledTaskTargetType, string> = {
  pipeline_instance: "purple",
  matter_reminder: "gold",
  pipeline_template: "geekblue",
  agent_run: "cyan",
};

/**
 * Map a thrown mutation error to a Chinese, business-legible toast message.
 *
 * 409 responses (invalid status transition, pipeline instance already
 * running — contract §6.5) carry backend-internal English text, so a
 * caller-supplied Chinese `conflictFallback` is used instead of leaking
 * `err.message`. Everything else routes through the shared `mapApiError`.
 */
export function describeTaskActionError(err: unknown, conflictFallback: string): string {
  if (isApiError(err) && err.status === 409) return conflictFallback;
  return mapApiError(err);
}
